import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../theme';
import { apiUrl, fileUrl, imageUrl } from '../api';

function BuyBackAdmin() {
  const { tokens } = useTheme();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState({ open: false, src: '' });
  const [drafts, setDrafts] = useState({}); // per-item estimate draft
  const [heroIdx, setHeroIdx] = useState({}); // per-item selected hero image index
  const [selectedId, setSelectedId] = useState(null); // currently opened item id (left panel)
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest' | 'oldest' for right list ordering
  const [filterKind, setFilterKind] = useState('all'); // 'all' | 'evaluare' | 'buyback'
  const rightListRef = useRef(null); // ref to the right list scroll container
  // Mobile responsive state
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileDetail, setShowMobileDetail] = useState(false);
  const token = localStorage.getItem('token');

  async function load() {
    setLoading(true);
    const res = await fetch(apiUrl('/api/buyback'), { headers: { Authorization: token || '' } });
    const data = await res.json();
    const arr = Array.isArray(data) ? data : [];
    setItems(arr);
    const d = {};
    for (const it of arr) d[it._id] = it.estimateDiff || '';
    setDrafts(d);
    // Select oldest by default if nothing is selected, or if the selected no longer exists
    if (arr.length) {
      const oldest = [...arr].sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0))[0];
      const stillExists = selectedId && arr.some((x) => x._id === selectedId);
      if (!stillExists && oldest?._id) setSelectedId(oldest._id);
    } else {
      setSelectedId(null);
    }
    setLoading(false);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Detect mobile viewport
  useEffect(() => {
    const check = () => {
      try {
        setIsMobile(window.innerWidth <= 768);
      } catch (e) {
        setIsMobile(false);
      }
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  useEffect(() => {
    if (!isMobile) setShowMobileDetail(false);
  }, [isMobile]);

  async function updateItem(id, patch) {
    await fetch(apiUrl(`/api/buyback/${id}/estimate`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: token || '' },
      body: JSON.stringify(patch),
    });
    load();
  }

  async function removeItem(id) {
    if (!window.confirm('Ștergi această cerere?')) return;
    await fetch(apiUrl(`/api/buyback/${id}`), { method: 'DELETE', headers: { Authorization: token || '' } });
    load();
  }

  function waLink(phone, msg) {
    const p = (phone || '').replace(/[^0-9]/g, '');
    const text = encodeURIComponent(msg);
    return `https://wa.me/${p}?text=${text}`;
  }

  function buildMsg(it, diff) {
    const title = it?.targetCar?.title || `${it?.targetCar?.brand || ''} ${it?.targetCar?.model || ''}`;
    const clientCar = [it.brand, it.model, it.year].filter(Boolean).join(' ');
    const shown = (diff && String(diff).trim()) || '—';
    return `Salut! Ai cerut buy-back/evaluare pentru ${clientCar || 'mașina ta'} și dorești ${title}. Diferența estimativă este ${shown} €. Ne confirmi că vrei să continuăm?`;
  }

  async function openWhatsApp(it) {
    const diff = drafts[it._id] ?? it.estimateDiff ?? '';
    const msg = buildMsg(it, diff);
    try {
      window.open(waLink(it.phone, msg), '_blank');
    } finally {
      await updateItem(it._id, { status: 'contacted', estimateDiff: diff });
    }
  }

  function HeaderBoxes({ it, compact, showClient = true, mobile = false }) {
    const title = it?.targetCar?.title || `${it?.targetCar?.brand || ''} ${it?.targetCar?.model || ''}`;
    const imgs = Array.isArray(it.images) && it.images.length > 0 ? it.images : (it.image ? [it.image] : []);
    // helpers: space-separated thousands formatting and unit suffixes
    const fmtDigits = (v) => {
      const s = String(v ?? '').replace(/[^0-9]/g, '');
      if (!s) return '';
      return s.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    };
    const fmtEuro = (v) => {
      const d = fmtDigits(v);
      return d ? `${d} €` : '';
    };
    const fmtKm = (v) => {
      const d = fmtDigits(v);
      return d ? `${d} km` : '';
    };
    return (
      <div style={{ display: 'grid', gap: compact ? 10 : 12 }}>
        {/* Dorește - entire box clickable */}
        {it.targetCar ? (compact ? (
          <div
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, background: tokens.surface, border: `1px solid ${tokens.border}`, padding: '8px 10px', borderRadius: 12, color: 'inherit' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              {it.targetCar?.image && (
                <img src={imageUrl(it.targetCar.image)} alt="ad" style={{ width: 56, height: 38, objectFit: 'cover', borderRadius: 10, border: `1px solid ${tokens.border}` }} />
              )}
              <div style={{ minWidth: 0 }}>
                <div style={{ color: tokens.text, fontWeight: 800, display: 'block', whiteSpace: 'normal', overflow: 'visible', textOverflow: 'clip' }}>
                  Dorește: {title}
                </div>
                <div style={{ color: tokens.textMuted, fontSize: 13 }}>{(it.targetCar?.model || '')}{it.targetCar?.year ? ` • ${it.targetCar.year}` : ''}</div>
              </div>
            </div>
            {/* price intentionally hidden in compact */}
          </div>
        ) : (
          <Link
            to={`/car/${it.targetCar?._id || ''}`}
            state={{ fromBuyBackAdmin: true }}
            onClick={(e) => e.stopPropagation()}
            className="bb-adbox"
            style={{
              display: 'flex',
              alignItems: mobile ? 'flex-start' : 'center',
              justifyContent: 'space-between',
              gap: 14,
              background: tokens.surface,
              border: `1px solid ${tokens.border}`,
              padding: '10px 12px',
              borderRadius: 12,
              color: 'inherit',
              textDecoration: 'none',
              flexWrap: mobile ? 'wrap' : 'nowrap',
              position: mobile ? 'relative' : 'static',
              paddingRight: mobile ? 96 : undefined,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: mobile ? '1 1 100%' : '0 0 auto' }}>
              {it.targetCar?.image && (
                <img src={imageUrl(it.targetCar.image)} alt="ad" style={{ width: 60, height: 42, objectFit: 'cover', borderRadius: 10, border: `1px solid ${tokens.border}` }} />
              )}
              <div style={{ minWidth: 0 }}>
                <div style={{ color: tokens.text, fontWeight: 800, display: 'block', whiteSpace: mobile ? 'normal' : 'nowrap', overflow: mobile ? 'visible' : 'hidden', textOverflow: mobile ? 'clip' : 'ellipsis' }}>
                  Dorește: {title}
                </div>
                <div style={{ color: tokens.textMuted, fontSize: 13 }}>{(it.targetCar?.model || '')}{it.targetCar?.year ? ` • ${it.targetCar.year}` : ''}</div>
              </div>
            </div>
            {it.targetCar?.price && (
              mobile ? (
                <div
                  style={{
                    position: 'absolute',
                    right: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(34,197,94,0.10)',
                    color: tokens.accent,
                    fontWeight: 900,
                    whiteSpace: 'nowrap',
                    borderRadius: 10,
                    padding: '3px 10px',
                    border: '1px solid rgba(34,197,94,0.35)'
                  }}
                  aria-label="Preț mașină de pe site"
                >
                  {fmtEuro(it.targetCar.price)}
                </div>
              ) : (
                <div style={{ color: tokens.accent, fontWeight: 800, whiteSpace: 'nowrap' }}>
                  {fmtEuro(it.targetCar.price)}
                </div>
              )
            )}
          </Link>
        )) : null}

        {/* Vrea evaluare la (optional) */}
        {showClient && (
          <div className={compact ? undefined : 'bb-adbox'} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: compact ? 10 : 14, background: tokens.surface, border: `1px solid ${tokens.border}`, padding: compact ? '8px 10px' : '10px 12px', borderRadius: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              {imgs[0] && (<img src={imageUrl(imgs[0])} alt="client" style={{ width: compact ? 56 : 60, height: compact ? 38 : 42, objectFit: 'cover', borderRadius: 10, border: `1px solid ${tokens.border}` }} />)}
              <div style={{ minWidth: 0 }}>
                <div style={{ color: tokens.text, fontWeight: 800, whiteSpace: compact ? 'normal' : 'nowrap', overflow: compact ? 'visible' : 'hidden', textOverflow: compact ? 'clip' : 'ellipsis' }}>Vrea evaluare la: {(it.brand || '').trim()} {(it.model || '').trim()} {it.year ? `• ${it.year}` : ''}</div>
                <div style={{ color: tokens.textMuted, fontSize: 13 }}>{fmtKm(it.km)}</div>
              </div>
            </div>
            {!compact && it.price && (<div style={{ color: tokens.accent, fontWeight: 800, whiteSpace: 'nowrap' }}>{fmtEuro(it.price)}</div>)}
          </div>
        )}
      </div>
    );
  }

  const selected = items.find((x) => x._id === selectedId) || null;
  const sortedItems = React.useMemo(() => {
    let arr = Array.isArray(items) ? [...items] : [];
    if (filterKind === 'evaluare') arr = arr.filter(x => !x.targetCar && (x.kind === 'evaluare' || !x.kind));
    else if (filterKind === 'buyback') arr = arr.filter(x => x.targetCar && (x.kind === 'buyback' || !x.kind));
    const cmp = (a, b) => {
      const da = new Date(a?.createdAt || 0).getTime();
      const db = new Date(b?.createdAt || 0).getTime();
      return sortOrder === 'newest' ? db - da : da - db;
    };
    try { arr.sort(cmp); } catch (e) {}
    return arr;
  }, [items, sortOrder, filterKind]);

  // When sort order changes, scroll the right list back to top
  useEffect(() => {
    if (rightListRef.current) {
      try { rightListRef.current.scrollTop = 0; } catch (e) {}
    }
  }, [sortOrder]);

  return (
  <div style={{ maxWidth: 1200, margin: '20px auto', padding: 16 }}>
      <h2 style={{ marginBottom: 12 }}>Trade-In / Evaluări</h2>
      {loading ? (
        <div>Se încarcă…</div>
      ) : isMobile ? (
        showMobileDetail && selected ? (
          <div onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowMobileDetail(false)} style={{ marginBottom: 12, padding: '8px 12px', background: tokens.panel, color: tokens.text, border: `1px solid ${tokens.border}`, borderRadius: 8, fontWeight: 700 }}>Înapoi la listă</button>
            {(() => {
              const it = selected;
              const submittedAt = it?.createdAt ? new Date(it.createdAt) : null;
              const submittedLabel = submittedAt ? submittedAt.toLocaleString('ro-RO', { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
              const isContacted = (it.status || '').toLowerCase() === 'contacted';
              const draft = drafts[it._id] ?? it.estimateDiff ?? '';
              const imgs = Array.isArray(it.images) && it.images.length > 0 ? it.images : (it.image ? [it.image] : []);
              const active = heroIdx[it._id] ?? 0;
              const imgSrc = imgs.length ? imageUrl(imgs[(active + imgs.length) % imgs.length]) : '';
              return (
                  <div
                  key={it._id}
                  className="bb-open-anim"
                  style={{
                    background: tokens.surface,
                    borderRadius: 18,
                      boxShadow: `0 6px 26px ${tokens.shadow}`,
                    padding: 20,
                      color: tokens.text,
                    overflow: 'hidden',
                    fontFamily: 'Inter, Segoe UI, sans-serif',
                    transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                      border: `1px solid ${tokens.border}`,
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <HeaderBoxes it={it} compact={false} showClient={!it.targetCar} mobile={true} />
                    {(() => {
                      const heroHeight = 220;
                      return (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, alignItems: 'start' }}>
                          <div style={{ borderRadius: 12, overflow: 'hidden', height: heroHeight, background: tokens.panel }}>
                            {imgSrc ? (
                              <img
                                className="bb-hero-anim"
                                src={imgSrc}
                                alt="client car"
                                onClick={() => setLightbox({ open: true, src: imgSrc })}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in', display: 'block' }}
                              />
                            ) : (
                              <div style={{ color: tokens.textMuted, padding: 40, textAlign: 'center' }}>Fără imagini</div>
                            )}
                          </div>
                          {imgs.length > 1 && (
                            <div className="nice-scroll thumbs-scroll" style={{ display: 'flex', gap: 8, overflowX: 'auto', overflowY: 'hidden', padding: '0 2px' }}>
                              {imgs.map((im, i) => (
                                <div key={i} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: i === (heroIdx[it._id] ?? 0) ? `2px solid ${tokens.accent}` : `1px solid ${tokens.border}` }}>
                                  <img src={imageUrl(im)} alt={`thumb-${i}`} onClick={() => setHeroIdx((prev) => ({ ...prev, [it._id]: i }))} style={{ width: 120, height: 68, objectFit: 'cover', cursor: 'pointer', display: 'block' }} />
                                </div>
                              ))}
                            </div>
                          )}
                          {it.talon && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <div style={{ fontSize: 12, color: tokens.textMuted, minWidth: 70 }}>Talon:</div>
                              <img src={imageUrl(it.talon)} alt="talon" style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 8, border: `1px solid ${tokens.border}`, background: tokens.panel }} />
                              <a href={fileUrl(`/uploads/${it.talon}`)} target="_blank" rel="noreferrer" style={{ color: tokens.accent, fontSize: 13 }}>Deschide</a>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    <div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, alignItems: 'start' }}>
                        <div>
                          <h3 style={{ fontSize: 18, fontWeight: 700, margin: '6px 0 4px' }}>{(it.brand || '').trim()} {(it.model || '').trim()} {it.year ? `• ${it.year}` : ''}</h3>
                          <p style={{ marginTop: 4, fontSize: 13, color: tokens.textMuted }}><b>Tel:</b> {it.phone} {submittedLabel && (<span style={{ color: tokens.textMuted }}> • Trimis la: {submittedLabel}</span>)}</p>
                        </div>
                        <div style={{ border: '1px solid rgba(34,197,94,0.35)', background: tokens.surface, borderRadius: 12, padding: '12px 14px', boxShadow: `0 0 0 2px rgba(34,197,94,0.15) inset, 0 8px 24px ${tokens.shadow}` }}>
                          <div style={{ fontSize: 12, color: tokens.accent, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Clientul cere pe mașină</div>
                          <div style={{ fontWeight: 900, color: tokens.accent, fontSize: 22, lineHeight: 1.1 }}>
                            {(() => { const s=String(it.price??'').replace(/[^0-9]/g,''); return s? s.replace(/\B(?=(\d{3})+(?!\d))/g,' ') + ' €' : '—'; })()}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 8, marginTop: 10 }}>
                        {(() => {
                          const onlyDigits = (v) => String(v ?? '').replace(/[^0-9]/g, '');
                          const withSpaces = (v) => { const s = onlyDigits(v); return s ? s.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : ''; };
                          const fmtKm = (v) => { const s = withSpaces(v); return s ? `${s} km` : ''; };
                          const fmtEuro = (v) => { const s = withSpaces(v); return s ? `${s} €` : ''; };
                          const fmtCp = (v) => { const s = withSpaces(v); return s ? `${s} cp` : ''; };
                          const fmtCm3 = (v) => { const s = withSpaces(v); return s ? `${s} cm3` : ''; };
                          return [
                            ['PREȚ', fmtEuro(it.pret || it.price)],
                            ['Tip', it.type],
                            ['KM', fmtKm(it.km)],
                            ['Combustibil', it.fuel],
                            ['Cutie', it.gearbox],
                            ['Capacitate', fmtCm3(it.capacity)],
                            ['Putere', fmtCp(it.power)],
                            ['An', it.year],
                          ].map(([label, val]) => (
                            <div key={label} style={{ border: `1px solid ${tokens.border}`, background: tokens.panel, borderRadius: 8, padding: '6px 8px' }}>
                              <div style={{ fontSize: 11, color: tokens.textMuted }}>{label}</div>
                              <div style={{ fontWeight: 700 }}>{val || '—'}</div>
                            </div>
                          ));
                        })()}
                      </div>
                      {it.description && (<p style={{ margin: '8px 0 0', fontSize: 14, color: tokens.text }}>{it.description}</p>)}
                    </div>
                    <div>
                      {!isContacted ? (
                        <div style={{ display: 'grid', gap: 10 }}>
                          <label style={{ display: 'grid', gap: 6 }}>
                            <span>Diferență estimativă (€)</span>
                            <input value={draft} onChange={(e) => setDrafts((prev) => ({ ...prev, [it._id]: e.target.value }))} style={{ padding: 8, borderRadius: 8, border: `1px solid ${tokens.mutedBorder}`, background: tokens.panel, color: tokens.text, width: '100%' }} />
                            {draft && (<div style={{ marginTop: 6, padding: '10px 12px', background: tokens.surface, border: `1px solid ${tokens.border}`, borderRadius: 12, color: tokens.text, fontWeight: 700 }}>{draft} €</div>)}
                          </label>
                          <button onClick={() => openWhatsApp(it)} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: 44, padding: '0 12px', background: '#25D366', color: '#0b0b0b', borderRadius: 8, border: '1px solid #1ea955', fontWeight: 800, textAlign: 'center', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '44px', width: '100%' }} title="Trimite pe WhatsApp oferta">Trimite pe WhatsApp oferta</button>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <input type="checkbox" checked={isContacted} onChange={(e) => updateItem(it._id, { status: e.target.checked ? 'contacted' : 'new', estimateDiff: drafts[it._id] ?? it.estimateDiff })} style={{ width: 22, height: 22 }} />
                              <span>Contactat</span>
                            </label>
                            <button onClick={() => removeItem(it._id)} style={{ width: 64, height: 64, background: '#ef4444', color: '#fff', borderRadius: 12, border: '1px solid #b91c1c', fontWeight: 800, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, cursor: 'pointer' }} title="Șterge">
                              <span style={{ fontSize: 11, lineHeight: 1 }}>Șterge</span>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" strokeWidth="2"/><path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gap: 10 }}>
                          <div>
                            <span>Diferență estimativă</span>
                            <div style={{ marginTop: 6, padding: '10px 12px', background: tokens.surface, border: `1px solid ${tokens.border}`, borderRadius: 12, color: tokens.text, fontWeight: 700 }}>{(it.estimateDiff || draft || '—')} €</div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <input type="checkbox" checked={isContacted} onChange={(e) => updateItem(it._id, { status: e.target.checked ? 'contacted' : 'new', estimateDiff: drafts[it._id] ?? it.estimateDiff })} style={{ width: 22, height: 22 }} />
                              <span>Contactat</span>
                            </label>
                            <button onClick={() => removeItem(it._id)} style={{ width: 70, height: 70, background: '#ef4444', color: '#fff', borderRadius: 12, border: '1px solid #b91c1c', fontWeight: 800, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, cursor: 'pointer' }} title="Șterge">
                              <span style={{ fontSize: 11, lineHeight: 1 }}>Șterge</span>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" strokeWidth="2"/><path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        ) : (
          <div onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ color: tokens.text, fontWeight: 700 }}>Total evaluări: {items.length}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: tokens.textMuted, fontSize: 13 }}>
                  <span>Sortare:</span>
                  <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} style={{ background: tokens.panel, color: tokens.text, border: `1px solid ${tokens.border}`, borderRadius: 8, padding: '6px 8px' }}>
                    <option value="newest">Cele mai noi</option>
                    <option value="oldest">Cele mai vechi</option>
                  </select>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: tokens.textMuted, fontSize: 13 }}>
                  <span>Tip:</span>
                  <select value={filterKind} onChange={(e) => setFilterKind(e.target.value)} style={{ background: tokens.panel, color: tokens.text, border: `1px solid ${tokens.border}`, borderRadius: 8, padding: '6px 8px' }}>
                    <option value="all">Toate</option>
                    <option value="evaluare">Evaluări</option>
                    <option value="buyback">Buy-back</option>
                  </select>
                </label>
              </div>
            </div>
            <div ref={rightListRef} className="nice-scroll" style={{ display: 'grid', gap: 12, maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden', paddingRight: 4, width: '100%', boxSizing: 'border-box', scrollbarGutter: 'stable both-edges', overscrollBehavior: 'contain' }}>
              {sortedItems.map((it) => (
                <div
                  key={it._id}
                  onClick={() => { setSelectedId(it._id); setShowMobileDetail(true); }}
                  className="bb-adbox"
                  style={{
                    background: tokens.surface,
                    border: `1px solid ${tokens.border}`,
                    borderRadius: 16,
                    boxShadow: `0 6px 20px ${tokens.shadow}${it._id === selectedId ? `, 0 0 0 2px ${tokens.accent} inset` : ''}`,
                    padding: 12,
                    color: tokens.text,
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                    width: '100%',
                    marginRight: 0,
                    overflow: 'visible',
                    minWidth: 0,
                  }}
                >
                  <HeaderBoxes it={it} compact={true} />
                </div>
              ))}
            </div>
          </div>
        )
      ) : (
        <div onClick={(e) => e.stopPropagation()} style={{ display: 'grid', gridTemplateColumns: 'minmax(520px, 760px) 400px', gap: 20, alignItems: 'start' }}>
          {/* Left detail panel */}
          <div>
            {selected ? (
              (() => {
                const it = selected;
                // const title = it?.targetCar?.title || `${it?.targetCar?.brand || ''} ${it?.targetCar?.model || ''}`;
                const submittedAt = it?.createdAt ? new Date(it.createdAt) : null;
                const submittedLabel = submittedAt ? submittedAt.toLocaleString('ro-RO', { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
                const isContacted = (it.status || '').toLowerCase() === 'contacted';
                const draft = drafts[it._id] ?? it.estimateDiff ?? '';
                const imgs = Array.isArray(it.images) && it.images.length > 0 ? it.images : (it.image ? [it.image] : []);
                const active = heroIdx[it._id] ?? 0;
                const imgSrc = imgs.length ? imageUrl(imgs[(active + imgs.length) % imgs.length]) : '';
                return (
                  <div
                    key={it._id}
                    className="bb-open-anim"
                    style={{
                      background: tokens.surface,
                      borderRadius: 18,
                      boxShadow: `0 6px 26px ${tokens.shadow}`,
                      padding: 20,
                      color: tokens.text,
                      overflow: 'hidden',
                      fontFamily: 'Inter, Segoe UI, sans-serif',
                      transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                      border: `1px solid ${tokens.border}`,
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      <HeaderBoxes it={it} compact={false} showClient={!it.targetCar} />

                      {/* Imagine principală + Thumbnails at right */}
                      {(() => {
                        const heroHeight = 240;
                        return (
                          <div style={{ display: 'grid', gridTemplateColumns: imgs.length > 1 ? '1fr 240px' : '1fr', gap: 12, alignItems: 'start' }}>
                            <div style={{ borderRadius: 12, overflow: 'hidden', height: heroHeight, background: tokens.panel }}>
                              {imgSrc ? (
                                <img
                                  className="bb-hero-anim"
                                  src={imgSrc}
                                  alt="client car"
                                  onClick={() => setLightbox({ open: true, src: imgSrc })}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in', display: 'block' }}
                                />
                              ) : (
                                <div style={{ color: tokens.textMuted, padding: 40, textAlign: 'center' }}>Fără imagini</div>
                              )}
                            </div>

                            {imgs.length > 1 && (
                              <div
                                className="nice-scroll thumbs-scroll"
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(3, 1fr)',
                                  gap: 8,
                                  maxHeight: heroHeight,
                                  overflowY: 'auto',
                                  overflowX: 'hidden',
                                  paddingRight: 2,
                                }}
                              >
                                {imgs.map((im, i) => (
                                  <div key={i} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: i === (heroIdx[it._id] ?? 0) ? `2px solid ${tokens.accent}` : `1px solid ${tokens.border}` }}>
                                    <img
                                      src={imageUrl(im)}
                                      alt={`thumb-${i}`}
                                      onClick={() => setHeroIdx((prev) => ({ ...prev, [it._id]: i }))}
                                      style={{ width: '100%', height: 68, objectFit: 'cover', cursor: 'pointer', display: 'block' }}
                                    />
                                  </div>
                                ))}
                              </div>
                            )}
                            {it.talon && (
                              <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ fontSize: 12, color: tokens.textMuted, minWidth: 80 }}>Poză talon:</div>
                                <img src={fileUrl(`/uploads/${it.talon}`)} alt="talon" style={{ width: 140, height: 96, objectFit: 'cover', borderRadius: 8, border: `1px solid ${tokens.border}`, background: tokens.panel }} />
                                <a href={fileUrl(`/uploads/${it.talon}`)} target="_blank" rel="noreferrer" style={{ color: tokens.accent, fontSize: 13 }}>Deschide</a>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Info */}
                      <div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 14, alignItems: 'start' }}>
                          <div>
                            <h3 style={{ fontSize: 18, fontWeight: 700, margin: '6px 0 4px' }}>{(it.brand || '').trim()} {(it.model || '').trim()} {it.year ? `• ${it.year}` : ''}</h3>
                            <p style={{ marginTop: 4, fontSize: 13, color: tokens.textMuted }}><b>Tel:</b> {it.phone} {submittedLabel && (<span style={{ color: tokens.textMuted }}> • Trimis la: {submittedLabel}</span>)}</p>
                          </div>
                          <div style={{ border: '1px solid rgba(34,197,94,0.35)', background: tokens.surface, borderRadius: 12, padding: '12px 14px', boxShadow: `0 0 0 2px rgba(34,197,94,0.15) inset, 0 8px 24px ${tokens.shadow}` }}>
                            <div style={{ fontSize: 12, color: tokens.accent, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Clientul cere pe mașină</div>
                            <div style={{ fontWeight: 900, color: tokens.accent, fontSize: 22, lineHeight: 1.1 }}>
                              {(() => { const s=String(it.price??'').replace(/[^0-9]/g,''); return s? s.replace(/\B(?=(\d{3})+(?!\d))/g,' ') + ' €' : '—'; })()}
                            </div>
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 8, marginTop: 10 }}>
                          {(() => {
                            const onlyDigits = (v) => String(v ?? '').replace(/[^0-9]/g, '');
                            const withSpaces = (v) => {
                              const s = onlyDigits(v);
                              return s ? s.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : '';
                            };
                            const fmtKm = (v) => {
                              const s = withSpaces(v);
                              return s ? `${s} km` : '';
                            };
                            // const fmtEuro = (v) => {
                            //   const s = withSpaces(v);
                            //   return s ? `${s} €` : '';
                            // };
                            const fmtCp = (v) => {
                              const s = withSpaces(v);
                              return s ? `${s} cp` : '';
                            };
                            const fmtCm3 = (v) => {
                              const s = withSpaces(v);
                              return s ? `${s} cm3` : '';
                            };
                            return [
                              ['Tip', it.type],
                              ['KM', fmtKm(it.km)],
                              ['Combustibil', it.fuel],
                              ['Cutie', it.gearbox],
                              ['Capacitate', fmtCm3(it.capacity)],
                              ['Putere', fmtCp(it.power)],
                              ['An', it.year],
                            ].map(([label, val]) => (
                              <div key={label} style={{ border: `1px solid ${tokens.border}`, background: tokens.panel, borderRadius: 8, padding: '6px 8px' }}>
                                <div style={{ fontSize: 11, color: tokens.textMuted }}>{label}</div>
                                <div style={{ fontWeight: 700 }}>{val || '—'}</div>
                              </div>
                            ));
                          })()}
                        </div>

                        {it.description && (<p style={{ margin: '8px 0 0', fontSize: 14, color: tokens.text }}>{it.description}</p>)}
                      </div>

                      {/* row 4: estimate */}
                      <div>
                        {!isContacted ? (
                          <div style={{ display: 'grid', gap: 10 }}>
                            <label style={{ display: 'grid', gap: 6 }}>
                              <span>Diferență estimativă (€)</span>
                              <input value={draft} onChange={(e) => setDrafts((prev) => ({ ...prev, [it._id]: e.target.value }))} style={{ padding: 8, borderRadius: 8, border: `1px solid ${tokens.mutedBorder}`, background: tokens.panel, color: tokens.text, width: '100%' }} />
                              {draft && (<div style={{ marginTop: 6, padding: '10px 12px', background: tokens.surface, border: `1px solid ${tokens.border}`, borderRadius: 12, color: tokens.text, fontWeight: 700 }}>{draft} €</div>)}
                            </label>

                            <button onClick={() => openWhatsApp(it)} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: 42, padding: '0 12px', background: '#25D366', color: '#0b0b0b', borderRadius: 8, border: '1px solid #1ea955', fontWeight: 800, textAlign: 'center', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '42px' }} title="Trimite pe WhatsApp oferta">Trimite pe WhatsApp oferta</button>

                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <input type="checkbox" checked={isContacted} onChange={(e) => updateItem(it._id, { status: e.target.checked ? 'contacted' : 'new', estimateDiff: drafts[it._id] ?? it.estimateDiff })} style={{ width: 22, height: 22 }} />
                                <span>Contactat</span>
                              </label>
                              <button onClick={() => removeItem(it._id)} style={{ width: 70, height: 70, background: '#ef4444', color: '#fff', borderRadius: 12, border: '1px solid #b91c1c', fontWeight: 800, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, cursor: 'pointer' }} title="Șterge">
                                <span style={{ fontSize: 11, lineHeight: 1 }}>Șterge</span>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" strokeWidth="2"/><path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div style={{ display: 'grid', gap: 10 }}>
                            <div>
                              <span>Diferență estimativă</span>
                              <div style={{ marginTop: 6, padding: '10px 12px', background: tokens.surface, border: `1px solid ${tokens.border}`, borderRadius: 12, color: tokens.text, fontWeight: 700 }}>{(it.estimateDiff || draft || '—')} €</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <input type="checkbox" checked={isContacted} onChange={(e) => updateItem(it._id, { status: e.target.checked ? 'contacted' : 'new', estimateDiff: drafts[it._id] ?? it.estimateDiff })} style={{ width: 22, height: 22 }} />
                                <span>Contactat</span>
                              </label>
                              <button onClick={() => removeItem(it._id)} style={{ width: 70, height: 70, background: '#ef4444', color: '#fff', borderRadius: 12, border: '1px solid #b91c1c', fontWeight: 800, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, cursor: 'pointer' }} title="Șterge">
                                <span style={{ fontSize: 11, lineHeight: 1 }}>Șterge</span>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" strokeWidth="2"/><path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()
            ) : (
              <div style={{ color: tokens.textMuted }}>Selectează o evaluare din lista din dreapta.</div>
            )}
          </div>

          {/* Right sidebar list */}
          <div style={{ minHeight: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ color: tokens.text, fontWeight: 700 }}>Total evaluări: {items.length}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: tokens.textMuted, fontSize: 13 }}>
                  <span>Sortare:</span>
                  <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} style={{ background: tokens.panel, color: tokens.text, border: `1px solid ${tokens.border}`, borderRadius: 8, padding: '6px 8px' }}>
                    <option value="newest">Cele mai noi</option>
                    <option value="oldest">Cele mai vechi</option>
                  </select>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: tokens.textMuted, fontSize: 13 }}>
                  <span>Tip:</span>
                  <select value={filterKind} onChange={(e) => setFilterKind(e.target.value)} style={{ background: tokens.panel, color: tokens.text, border: `1px solid ${tokens.border}`, borderRadius: 8, padding: '6px 8px' }}>
                    <option value="all">Toate</option>
                    <option value="evaluare">Evaluări</option>
                    <option value="buyback">Buy-back</option>
                  </select>
                </label>
              </div>
            </div>
            <div ref={rightListRef} className="nice-scroll" style={{ display: 'grid', gap: 12, maxHeight: 420, overflowY: 'auto', overflowX: 'hidden', paddingRight: 4, width: '100%', boxSizing: 'border-box', scrollbarGutter: 'stable both-edges', overscrollBehavior: 'contain' }}>
              {sortedItems.map((it) => (
                <div
                  key={it._id}
                  onClick={() => setSelectedId(it._id)}
                  className="bb-adbox"
                  style={{
                    background: tokens.surface,
                    border: `1px solid ${tokens.border}`,
                    borderRadius: 16,
                    boxShadow: `0 6px 20px ${tokens.shadow}${it._id === selectedId ? `, 0 0 0 2px ${tokens.accent} inset` : ''}`,
                    padding: 12,
                    color: tokens.text,
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                    width: '100%',
                    marginRight: 0,
                    overflow: 'visible',
                    minWidth: 0,
                  }}
                >
                  <HeaderBoxes it={it} compact={true} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div style={{ marginTop: 16 }}>
  <Link to="/dashboard" style={{ color: tokens.accent }}>Înapoi la anunțuri</Link>
      </div>

      {lightbox.open && (
        <div onClick={() => setLightbox({ open: false, src: '' })} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <img src={lightbox.src} alt="zoom" style={{ maxWidth: '95vw', maxHeight: '90vh', borderRadius: 8, boxShadow: '0 10px 40px #000' }} />
        </div>
      )}
    </div>
  );
}

export default BuyBackAdmin;
